/**
 * InstructorForm - Formularz dodawania/edycji instruktorów
 * wojskaspecjalne.pl
 */

import React, { useState, useEffect } from 'react';
import {
  createInstructor,
  updateInstructor,
  getInstructorById,
  type CreateInstructorData,
  type Instructor,
} from '../../lib/instructorService';
import {
  Button,
  Input,
  Textarea,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  AlertDescription,
  Separator,
} from '../ui';
import { X, Plus } from 'lucide-react';
import './InstructorForm.css';

interface InstructorFormProps {
  instructorId?: string; // Jeśli jest, to edytujemy
  onSuccess?: (instructor: Instructor) => void;
  onCancel?: () => void;
}

export const InstructorForm: React.FC<InstructorFormProps> = ({
  instructorId,
  onSuccess,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(!!instructorId);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<{
    name: string;
    bio: string;
    email: string;
    phone: string;
    website_url: string;
    specialization: string[];
    experience_years: string;
    photo_url: string;
    is_active: boolean;
    display_order: string;
  }>({
    name: '',
    bio: '',
    email: '',
    phone: '',
    website_url: '',
    specialization: [],
    experience_years: '',
    photo_url: '',
    is_active: true,
    display_order: '0',
  });

  const [newSpecialization, setNewSpecialization] = useState('');

  // Load instructor data if editing
  useEffect(() => {
    if (instructorId) {
      loadInstructorData();
    }
  }, [instructorId]);

  const loadInstructorData = async () => {
    if (!instructorId) return;

    try {
      setLoadingData(true);
      const result = await getInstructorById(instructorId);

      if (result.success && result.data) {
        const instructor = result.data;

        setFormData({
          name: instructor.name || '',
          bio: instructor.bio || '',
          email: instructor.email || '',
          phone: instructor.phone || '',
          website_url: instructor.website_url || '',
          specialization: instructor.specialization || [],
          experience_years: instructor.experience_years?.toString() || '',
          photo_url: instructor.photo_url || '',
          is_active: instructor.is_active ?? true,
          display_order: instructor.display_order?.toString() || '0',
        });
      } else {
        setError(result.error || 'Nie udało się załadować danych instruktora');
      }
    } catch (err: any) {
      console.error('Error loading instructor:', err);
      setError(err.message || 'Błąd podczas ładowania danych');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const instructorData: CreateInstructorData = {
        name: formData.name.trim(),
        bio: formData.bio.trim() || null,
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        website_url: formData.website_url.trim() || null,
        specialization: formData.specialization.length > 0 ? formData.specialization : undefined,
        experience_years: formData.experience_years ? parseInt(formData.experience_years, 10) : null,
        photo_url: formData.photo_url.trim() || null,
        is_active: formData.is_active,
        display_order: formData.display_order ? parseInt(formData.display_order, 10) : 0,
      };

      let result;
      if (instructorId) {
        result = await updateInstructor({
          id: instructorId,
          ...instructorData,
        });
      } else {
        result = await createInstructor(instructorData);
      }

      if (result.success && result.data) {
        if (onSuccess) {
          onSuccess(result.data);
        }
      } else {
        setError(result.error || 'Nie udało się zapisać instruktora');
      }
    } catch (err: any) {
      console.error('Error saving instructor:', err);
      setError(err.message || 'Błąd podczas zapisywania');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSpecialization = () => {
    if (newSpecialization.trim() && !formData.specialization.includes(newSpecialization.trim())) {
      setFormData({
        ...formData,
        specialization: [...formData.specialization, newSpecialization.trim()],
      });
      setNewSpecialization('');
    }
  };

  const handleRemoveSpecialization = (index: number) => {
    setFormData({
      ...formData,
      specialization: formData.specialization.filter((_, i) => i !== index),
    });
  };

  if (loadingData) {
    return (
      <Card variant="soft" className="instructor-form">
        <CardContent>
          <p>Ładowanie danych instruktora...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="soft" className="instructor-form">
      <CardHeader>
        <CardTitle>
          {instructorId ? 'Edytuj instruktora' : 'Dodaj nowego instruktora'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="instructor-form-content">
          {error && (
            <Alert variant="error" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="form-section">
            <label htmlFor="name" className="form-label">
              Imię i nazwisko <span className="required">*</span>
            </label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="np. Jan Kowalski"
              size="3"
            />
          </div>

          <div className="form-section">
            <label htmlFor="bio" className="form-label">
              Biografia
            </label>
            <Textarea
              id="bio"
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              placeholder="Opisz doświadczenie i kwalifikacje instruktora..."
              rows={5}
              className="form-textarea"
            />
          </div>

          <div className="form-row">
            <div className="form-section">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="instruktor@example.com"
                size="3"
              />
            </div>

            <div className="form-section">
              <label htmlFor="phone" className="form-label">
                Telefon
              </label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+48 123 456 789"
                size="3"
              />
            </div>
          </div>

          <div className="form-section">
            <label htmlFor="website_url" className="form-label">
              Strona internetowa
            </label>
            <Input
              id="website_url"
              type="url"
              value={formData.website_url}
              onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
              placeholder="https://example.com"
              size="3"
            />
          </div>

          <div className="form-section">
            <label htmlFor="photo_url" className="form-label">
              URL zdjęcia
            </label>
            <Input
              id="photo_url"
              type="url"
              value={formData.photo_url}
              onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
              placeholder="https://example.com/photo.jpg"
              size="3"
            />
          </div>

          <div className="form-section">
            <label className="form-label">Specjalizacje</label>
            <div className="specialization-input-group">
              <Input
                type="text"
                value={newSpecialization}
                onChange={(e) => setNewSpecialization(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddSpecialization();
                  }
                }}
                placeholder="np. obsługa broni, survival, medycyna pola walki"
                size="3"
              />
              <Button
                type="button"
                variant="outline"
                size="3"
                onClick={handleAddSpecialization}
                disabled={!newSpecialization.trim()}
              >
                <Plus size={16} aria-hidden="true" />
                Dodaj
              </Button>
            </div>
            {formData.specialization.length > 0 && (
              <div className="specialization-tags">
                {formData.specialization.map((spec, index) => (
                  <span key={index} className="specialization-tag">
                    {spec}
                    <button
                      type="button"
                      onClick={() => handleRemoveSpecialization(index)}
                      className="specialization-tag-remove"
                      aria-label={`Usuń ${spec}`}
                    >
                      <X size={14} aria-hidden="true" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-section">
              <label htmlFor="experience_years" className="form-label">
                Lata doświadczenia
              </label>
              <Input
                id="experience_years"
                type="number"
                min="0"
                value={formData.experience_years}
                onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })}
                placeholder="10"
                size="3"
              />
            </div>

            <div className="form-section">
              <label htmlFor="display_order" className="form-label">
                Kolejność wyświetlania
              </label>
              <Input
                id="display_order"
                type="number"
                min="0"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                placeholder="0"
                size="3"
              />
            </div>
          </div>

          <div className="form-section">
            <label className="form-checkbox-label">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="form-checkbox"
              />
              <span>Instruktor aktywny (widoczny publicznie)</span>
            </label>
          </div>

          <Separator className="my-4" />

          <div className="form-actions">
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={loading}
              >
                Anuluj
              </Button>
            )}
            <Button type="submit" variant="solid" disabled={loading}>
              {loading ? 'Zapisywanie...' : instructorId ? 'Zaktualizuj' : 'Dodaj instruktora'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

